import { CheckIcon } from '@heroicons/react/24/solid';
import { CellContext } from '@tanstack/react-table';
import { useState, useEffect, ChangeEvent } from 'react';
import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import { twMerge } from 'tailwind-merge';

import IndeterminateCheckbox from '../indeterminate-checkbox';
import { TableOption, TableCellValue } from './type';

export function TableCell<TData>({
  getValue,
  row,
  column,
  table,
}: CellContext<TData, TableCellValue>) {
  const initialValue = getValue();
  const columnMeta = column.columnDef.meta;
  const tableMeta = table.options.meta;
  const [value, setValue] = useState(initialValue);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = (e: ChangeEvent<HTMLInputElement>) => {
    displayValidationMessage(e);
    if (columnMeta?.validate) {
      let newValue: any = e.target.value;
      if (columnMeta.type === 'number') {
        newValue = parseInt(newValue, 10);
      }
      const isValid = columnMeta.validate(newValue);
      tableMeta?.updateData(row.index, column.id, newValue, isValid);
    } else {
      tableMeta?.updateData(
        row.index,
        column.id,
        value,
        e.target.validity.valid,
      );
    }
  };

  const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.checked);
    tableMeta?.updateData(row.index, column.id, e.target.checked, true);
  };

  const onSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    displayValidationMessage(e);
    setValue(e.target.value);
    tableMeta?.updateData(
      row.index,
      column.id,
      e.target.value,
      e.target.validity.valid,
    );
  };

  const displayValidationMessage = <
    T extends HTMLInputElement | HTMLSelectElement,
  >(
    e: ChangeEvent<T>,
  ) => {
    if (columnMeta?.validate) {
      const isValid = columnMeta.validate(e.target.value);
      if (isValid) {
        e.target.setCustomValidity('');
        setValidationMessage('');
      } else {
        e.target.setCustomValidity(columnMeta.validationMessage || '');
        setValidationMessage(columnMeta.validationMessage || '');
      }
    } else if (e.target.validity.valid) {
      setValidationMessage('');
    } else {
      setValidationMessage(e.target.validationMessage);
    }
  };

  if (columnMeta?.canEdit !== false && tableMeta?.editedRows[row.id]) {
    if (columnMeta?.renderEditCell) {
      return columnMeta.renderEditCell({ getValue, row, column, table } as any);
    }
    if (columnMeta?.type === 'select')
      return (
        <div className="flex flex-col items-start gap-1">
          <FormSelect
            name={column.id}
            onChange={onSelectChange}
            value={initialValue as any}
            options={columnMeta?.options}
            title={validationMessage}
            className={twMerge([
              columnMeta?.className,
              !!validationMessage && 'border-2 border-red-500',
            ])}
            data-testid={`table-cell-select-${column.id}-${row.id}`}
          />
          {validationMessage && (
            <span
              data-testid={`table-cell-select-error-${column.id}-${row.id}`}
              className="text-red-500"
            >
              {validationMessage}
            </span>
          )}
        </div>
      );

    if (columnMeta?.type === 'checkbox')
      return (
        <div
          className={twMerge([
            'flex flex-col items-start gap-1',
            columnMeta?.className,
            columnMeta?.type === 'checkbox' && 'justify-center',
          ])}
          onClick={(e) => setValue(!value)}
        >
          <IndeterminateCheckbox
            data-testid={`table-cell-checkbox-${column.id}-${row.id}`}
            id={`${column.id}-${row.id}`}
            name={`${column.id}-${row.index}`}
            checked={value as boolean}
            onChange={onCheckboxChange}
            className={twMerge([
              !!validationMessage && 'border-2 border-red-500',
            ])}
          />
          {validationMessage && (
            <span
              data-testid={`table-cell-checkbox-error-${column.id}-${row.id}`}
              className="text-red-500"
            >
              {validationMessage}
            </span>
          )}
        </div>
      );

    return (
      <div
        className={twMerge([
          'flex flex-col items-start gap-1',
          columnMeta?.className,
          columnMeta?.type === 'checkbox' && 'justify-center',
        ])}
      >
        <FormInput
          id={column.id}
          name={column.id}
          value={value as any}
          onChange={(e) => setValue(e.target.value)}
          onBlur={onBlur}
          wrapperClassName="w-auto"
          className={twMerge([
            columnMeta?.type === 'color' && 'w-10 rounded-none p-0.5',
            columnMeta?.className,
            !!validationMessage && 'border-2 border-red-500',
          ])}
          type={columnMeta?.type || 'text'}
          required={columnMeta?.required}
          title={validationMessage}
          data-testid={`table-cell-input-${column.id}-${row.id}`}
        />
        {validationMessage && (
          <span
            data-testid={`table-cell-input-error-${column.id}-${row.id}`}
            className="text-red-500"
          >
            {validationMessage}
          </span>
        )}
      </div>
    );
  }

  if (columnMeta?.renderCell) {
    return columnMeta.renderCell({ getValue, row, column, table } as any);
  }

  if (columnMeta && columnMeta.type === 'select') {
    const option = columnMeta.options?.find(
      (o: TableOption) => o.value === value,
    );
    return <span className={columnMeta?.className || ''}>{option?.label}</span>;
  }

  if (columnMeta && columnMeta.type === 'color') {
    return (
      <div className="flex flex-col items-start gap-1">
        <span
          data-testid={`table-cell-color-${column.id}-${row.id}`}
          className={twMerge([
            'inline-block h-5 w-5 rounded-full border border-black',
            columnMeta?.className,
          ])}
          style={{ backgroundColor: value.toString() }}
        />
        {validationMessage && (
          <span
            data-testid={`table-cell-color-error-${column.id}-${row.id}`}
            className="text-red-500"
          >
            {validationMessage}
          </span>
        )}
      </div>
    );
  }

  if (columnMeta && columnMeta.type === 'checkbox') {
    return (
      <div
        data-testid={`table-cell-checkbox-${column.id}-${row.id}`}
        className={twMerge([
          'flex flex-col items-start gap-1',
          columnMeta?.className,
        ])}
      >
        {value ? <CheckIcon className="h-5 w-5 text-green-500" /> : ''}
        {validationMessage && (
          <span
            data-testid={`table-cell-checkbox-error-${column.id}-${row.id}`}
            className="text-red-500"
          >
            {validationMessage}
          </span>
        )}
      </div>
    );
  }

  return (
    <span
      data-testid={`table-cell-text-${column.id}-${row.id}`}
      title={value != null ? String(value) : undefined}
      className={columnMeta?.className || ''}
    >
      {value}
    </span>
  );
}
